import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import App from './App';
import { AuthProvider, CreditsProvider } from './contexts';

// ---------------------------------------------------------------------------
// Sentry initialisation — runs before React mounts
// ---------------------------------------------------------------------------
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE, // 'development' | 'production'
  enabled: import.meta.env.MODE === 'production', // Never report in dev

  // --- RATE LIMITS (protect free-tier quota) ---
  tracesSampleRate: 0.05, // 5% of page loads
  replaysSessionSampleRate: 0, // No replay on normal sessions
  replaysOnErrorSampleRate: 0.5, // 50% of error sessions get replay

  // --- QUOTA PROTECTION ---
  maxBreadcrumbs: 30,
  beforeSend(event) {
    // Drop events from known bot user-agents
    const ua = navigator.userAgent?.toLowerCase() || '';
    if (/bot|crawler|spider|headless/i.test(ua)) return null;

    // Client-side rate limit: max 10 events per minute per session
    const now = Date.now();
    const key = '__sentry_rate';
    const windowMs = 60_000;
    const max = 10;
    try {
      const timestamps: number[] = JSON.parse(
        sessionStorage.getItem(key) || '[]',
      ).filter((t: number) => now - t < windowMs);
      if (timestamps.length >= max) return null;
      timestamps.push(now);
      sessionStorage.setItem(key, JSON.stringify(timestamps));
    } catch {
      // sessionStorage unavailable (e.g. private browsing) — allow event
    }

    return event;
  },

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
  ],
});

// ---------------------------------------------------------------------------
// Top-level Error Boundary fallback (last resort safety net)
// ---------------------------------------------------------------------------
const GlobalFallback = () => (
  <div
    style={{
      padding: 48,
      textAlign: 'center',
      direction: 'rtl',
      fontFamily: 'sans-serif',
      color: '#e5e7eb',
      backgroundColor: '#0a0a0a',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <h2 style={{ fontSize: 28, marginBottom: 12 }}>אופס, משהו השתבש</h2>
    <p style={{ color: '#9ca3af', marginBottom: 24 }}>
      הצוות שלנו קיבל דיווח אוטומטי. נסה לרענן את הדף.
    </p>
    <button
      onClick={() => window.location.reload()}
      style={{
        padding: '12px 32px',
        backgroundColor: '#FF003C',
        color: '#fff',
        border: 'none',
        borderRadius: 8,
        fontWeight: 'bold',
        fontSize: 16,
        cursor: 'pointer',
      }}
    >
      רענן
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Could not find root element to mount to');
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={GlobalFallback} showDialog={false}>
      <BrowserRouter>
        <AuthProvider>
          <CreditsProvider>
            <App />
          </CreditsProvider>
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
